import React, { useEffect, useState } from 'react'
import {
  Table, Button, Input, Select, Row, Col, Card, Tag, Tooltip,
} from 'antd'
import {
  EyeOutlined, FileTextOutlined, PhoneOutlined, CalendarOutlined, ClockCircleOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useMasterData } from '@/context/MasterDataContext'
import { leadStore } from '@/pages/Lead/leadStore'
import { discTypeToContact, contactTypeColor, DCR_CONTACT_TYPES } from './dcrStore'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'

// One row per logged contact, derived from Lead / Customer discussion history.
interface DcrRow {
  key: string
  date: string
  time: string
  employeeName: string
  targetType: 'Lead' | 'Customer'
  targetId: string
  targetName: string
  contactType: string
  customerStatus?: string
  nextFollowUpDate?: string
  details: string
}

const toDate = (d: any) => (d instanceof Date ? d : new Date(d))
const dStr = (d: Date) => d.toISOString().split('T')[0]

const DCRList: React.FC = () => {
  const navigate = useNavigate()
  const { employees, consumers, masterDataLoaded } = useMasterData()
  const [rows, setRows] = useState<DcrRow[]>([])
  const [search, setSearch] = useState('')
  const [filterEmp, setFilterEmp] = useState<string>('')
  const [filterType, setFilterType] = useState<string>('')

  // Build DCR rows from the discussion history logged on each lead / customer.
  useEffect(() => {
    const build = () => {
      const out: DcrRow[] = []
      leadStore.getLeads().forEach(lead => {
        (lead.discussionHistory || []).forEach(disc => {
          const dt = toDate(disc.createdAt)
          out.push({
            key: `lead:${lead.id}:${disc.id}`,
            date: dStr(dt),
            time: dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            employeeName: disc.createdBy || '—',
            targetType: 'Lead',
            targetId: lead.id,
            targetName: lead.companyName || lead.customerName || lead.id,
            contactType: discTypeToContact[disc.type] || disc.type,
            customerStatus: disc.customerStatus,
            nextFollowUpDate: disc.nextFollowUpDate,
            details: disc.content || '',
          })
        })
      })
      consumers.forEach(cust => {
        (cust.discussionHistory || []).forEach(disc => {
          const dt = toDate(disc.createdAt)
          out.push({
            key: `customer:${cust.id}:${disc.id}`,
            date: dStr(dt),
            time: dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            employeeName: disc.createdBy || '—',
            targetType: 'Customer',
            targetId: cust.id,
            targetName: cust.name,
            contactType: discTypeToContact[disc.type] || disc.type,
            customerStatus: disc.customerStatus,
            nextFollowUpDate: disc.nextFollowUpDate,
            details: disc.content || '',
          })
        })
      })
      out.sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time))
      setRows(out)
    }
    build()
    return leadStore.subscribe(build)
  }, [consumers])

  const todayStr = dStr(new Date())
  const filtered = rows.filter(r => {
    const s = search.toLowerCase()
    const matchSearch = !s || r.employeeName.toLowerCase().includes(s) || r.targetName.toLowerCase().includes(s) || r.details.toLowerCase().includes(s)
    const matchEmp = !filterEmp || r.employeeName === filterEmp
    const matchType = !filterType || r.contactType === filterType
    return matchSearch && matchEmp && matchType
  })

  const totalContacts = rows.length
  const today = rows.filter(r => r.date === todayStr).length
  const followUpsDue = rows.filter(r => {
    if (!r.nextFollowUpDate) return false
    // nextFollowUpDate may be a full ISO string like "2026-08-08T18:30:00.000Z"
    // Normalise to YYYY-MM-DD before comparing with todayStr
    const followUpDateStr = r.nextFollowUpDate.split('T')[0]
    // Count as "due" if the follow-up date is today or in the past (overdue)
    return followUpDateStr <= todayStr
  }).length
  const leadsContacted = new Set(rows.filter(r => r.targetType === 'Lead').map(r => r.targetId)).size

  const columns = [
    {
      title: 'Date', dataIndex: 'date', key: 'date', width: 110,
      render: (d: string, r: DcrRow) => (
        <div>
          <div style={{ fontSize: 13, color: '#0B1426', fontWeight: 600 }}>{d}</div>
          <div style={{ fontSize: 11, color: '#8A93A6' }}>{r.time}</div>
        </div>
      ),
    },
    { title: 'Employee', dataIndex: 'employeeName', key: 'emp', width: 140,
      render: (n: string) => <span style={{ fontWeight: 600, color: '#0B1426' }}>{n}</span> },
    {
      title: 'Lead / Customer', key: 'target', width: 220,
      render: (_: any, r: DcrRow) => (
        <div>
          <div style={{ fontWeight: 600, color: '#1677ff', cursor: r.targetType === 'Lead' ? 'pointer' : 'default' }}
            onClick={() => r.targetType === 'Lead' && navigate(`/leads/${r.targetId}`)}>{r.targetName}</div>
          <Tag style={{ fontSize: 10, marginTop: 2 }} color={r.targetType === 'Lead' ? 'blue' : 'green'}>{r.targetType}</Tag>
        </div>
      ),
    },
    { title: 'Contact Type', dataIndex: 'contactType', key: 'contactType', width: 140,
      render: (t: string) => <Tag style={{ borderRadius: 100, fontWeight: 600, border: 'none', color: '#fff', background: contactTypeColor[t] || '#4A5568' }}>{t}</Tag> },
    { title: 'Customer Status', dataIndex: 'customerStatus', key: 'status', width: 150,
      render: (s?: string) => s ? <Tag color="default" style={{ borderRadius: 6 }}>{s}</Tag> : <span style={{ color: '#cbd2dd' }}>—</span> },
    {
      title: 'Next Follow-up', dataIndex: 'nextFollowUpDate', key: 'followup', width: 130,
      render: (d?: string) => {
        if (!d) return <span style={{ color: '#cbd2dd' }}>—</span>
        const overdue = d < todayStr
        return <span style={{ fontSize: 12, fontWeight: overdue ? 700 : 400, color: overdue ? '#DC2626' : '#0D9488' }}>{overdue && '⚠ '}{d}</span>
      },
    },
    {
      title: 'Action', key: 'action', width: 70,
      render: (_: any, r: DcrRow) => r.targetType === 'Lead'
        ? <Tooltip title="Open lead"><Button type="link" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/leads/${r.targetId}`)} /></Tooltip>
        : null,
    },
  ]

  if (!masterDataLoaded || !leadStore.loaded) return <TableSkeleton columns={7} />

  return (
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom: 20 }}>
        <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <PhoneOutlined style={{ color: '#001A4D' }} /> Daily Call Reports
        </h2>
        <p className="page-subtitle">Discussion history logged on leads &amp; customers — every contact appears here automatically.</p>
      </div>

      <KpiCardRow
        cards={[
          {
            label: 'Total Contacts',
            value: totalContacts,
            icon: <FileTextOutlined style={{ fontSize: 24, color: '#001A4D' }} />,
            iconBg: 'rgba(0,26,77,0.08)',
            borderColor: '#001A4D',
          },
          {
            label: 'Today',
            value: today,
            icon: <CalendarOutlined style={{ fontSize: 24, color: '#0099CC' }} />,
            iconBg: 'rgba(0,153,204,0.08)',
            borderColor: '#0099CC',
          },
          {
            label: 'Follow-ups Due',
            value: followUpsDue,
            icon: <ClockCircleOutlined style={{ fontSize: 24, color: '#D97706' }} />,
            iconBg: 'rgba(217,119,6,0.08)',
            borderColor: '#D97706',
          },
          {
            label: 'Leads Contacted',
            value: leadsContacted,
            icon: <PhoneOutlined style={{ fontSize: 24, color: '#00A651' }} />,
            iconBg: 'rgba(0,166,81,0.08)',
            borderColor: '#00A651',
          },
        ]}
      />

      <Card bordered={false} style={{ borderRadius: 8, marginBottom: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: '1px solid #E4E8F0' }} bodyStyle={{ padding: 14 }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} md={8}>
            <Input.Search placeholder="Search employee, lead/customer, details..." value={search} onChange={e => setSearch(e.target.value)} allowClear />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select placeholder="Employee" allowClear value={filterEmp || undefined} onChange={v => setFilterEmp(v ?? '')} style={{ width: '100%' }}
              options={Array.from(new Set(employees.map(e => e.name))).map(e => ({ label: e, value: e }))} />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select placeholder="Contact Type" allowClear value={filterType || undefined} onChange={v => setFilterType(v ?? '')} style={{ width: '100%' }}
              options={DCR_CONTACT_TYPES.map(t => ({ label: t, value: t }))} />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Button block onClick={() => { setSearch(''); setFilterEmp(''); setFilterType('') }}>Clear</Button>
          </Col>
        </Row>
      </Card>

      <Card bordered={false} style={{ borderRadius: 10, boxShadow: '0 1px 6px rgba(0,0,0,0.08)' }}>
        <div style={{ fontSize: 12, color: '#8A93A6', marginBottom: 10, textAlign: 'right' }}>
          Showing <strong>{filtered.length}</strong> of <strong>{rows.length}</strong> contacts
        </div>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="key"
          pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '25', '50', '100'], showSizeChanger: true, showTotal: (t, r) => `${r[0]}–${r[1]} of ${t} contacts` }}
          locale={{ emptyText: 'No contacts logged yet. Log calls/visits from a Lead and they will appear here.' }}
          expandable={{
            expandedRowRender: (r: DcrRow) => (
              <div style={{ padding: '4px 8px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#8A93A6', marginBottom: 4 }}>Discussion Details</div>
                <div style={{ fontSize: 13, color: '#0B1426', whiteSpace: 'pre-wrap' }}>{r.details || '—'}</div>
              </div>
            ),
          }}
        />
      </Card>
    </div>
  )
}

export default DCRList
